import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BedDouble, Building2, Layers, MapPin, Phone, Check } from "lucide-react";
import { FadeUp } from "./Reveal";
import { HOSPITAL, IMAGES } from "../data/content";
import { useLang } from "../i18n/i18n";

export function GodhraHospital() {
  const { t } = useLang();
  const h = t.hospital;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 60]);

  const highlights = [
    { icon: BedDouble, value: "100", label: h.beds },
    { icon: Building2, value: "1", label: h.corporate },
    { icon: Layers, value: "10+", label: h.roof },
  ];

  return (
    <section
      data-testid="godhra-hospital"
      className="relative overflow-hidden bg-ink2 px-6 py-28 md:px-12 md:py-36"
    >
      <div className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-bluecore/20 blur-[150px]" />
      <div className="pointer-events-none absolute -left-32 bottom-0 h-[420px] w-[420px] rounded-full bg-crimson/10 blur-[140px]" />

      <div className="relative mx-auto max-w-[1500px]">
        {/* Header */}
        <FadeUp>
          <div className="flex items-center gap-4">
            <span className="h-px w-12 bg-crimson" />
            <span className="font-mono text-xs uppercase tracking-[0.28em] text-crimson">
              {h.kicker} · {h.role}
            </span>
          </div>
        </FadeUp>

        <div className="mt-10 grid items-start gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Building image */}
          <FadeUp>
            <div ref={ref} className="relative">
              <div className="absolute -inset-3 border border-crimson/25" />
              <div className="clip-corner relative aspect-[16/10] w-full overflow-hidden bg-ink">
                <motion.img
                  style={{ y: imgY }}
                  src={IMAGES.hospitalBuilding}
                  alt={HOSPITAL.name}
                  loading="lazy"
                  decoding="async"
                  className="h-[112%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink2/60 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 left-6 z-10 border border-white/10 bg-ink/90 px-5 py-3 backdrop-blur-md">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson">
                  Godhra · Panchmahal
                </p>
              </div>
            </div>
          </FadeUp>

          {/* Text */}
          <div>
            <FadeUp>
              <h2 className="font-serif text-4xl font-light leading-tight text-white md:text-5xl">
                {h.title}
              </h2>
              <p className="mt-3 font-serif text-xl italic text-crimson">
                {h.tagline}
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <p className="mt-6 leading-relaxed text-white/70">{h.lead}</p>
            </FadeUp>
            <FadeUp delay={0.16}>
              <p className="mt-4 leading-relaxed text-white/50">{h.body}</p>
            </FadeUp>

            <FadeUp delay={0.22}>
              <div className="mt-8 grid grid-cols-3 gap-px border border-white/10 bg-white/10">
                {highlights.map((hl) => (
                  <div key={hl.label} className="bg-ink2 p-5 text-center">
                    <hl.icon className="mx-auto h-5 w-5 text-crimson" />
                    <p className="mt-3 font-serif text-3xl font-light text-white">{hl.value}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase leading-tight tracking-[0.12em] text-white/45">
                      {hl.label}
                    </p>
                  </div>
                ))}
              </div>
            </FadeUp>
          </div>
        </div>

        {/* Scope of services */}
        <FadeUp delay={0.1}>
          <div className="mt-20">
            <div className="mb-8 flex items-center gap-4">
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-white/60">
                {h.scopeTitle}
              </span>
              <span className="h-px flex-1 bg-white/10" />
            </div>
            <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
              {h.services.map((s, i) => (
                <div
                  key={i}
                  className="group flex items-center gap-3 bg-ink2 p-5 transition-colors duration-300 hover:bg-ink"
                >
                  <Check className="h-4 w-4 shrink-0 text-crimson" />
                  <span className="text-sm text-white/80">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </FadeUp>

        {/* Address + call */}
        <FadeUp delay={0.15}>
          <div className="mt-10 flex flex-col items-start justify-between gap-6 border border-white/10 bg-ink p-8 md:flex-row md:items-center">
            <div className="flex items-start gap-4">
              <MapPin className="mt-1 h-5 w-5 shrink-0 text-crimson" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                  {h.addressLabel}
                </p>
                <p className="mt-1 text-white/80">{HOSPITAL.address}</p>
              </div>
            </div>
            <a
              href={HOSPITAL.phone1Href}
              data-testid="hospital-call-button"
              className="group flex shrink-0 items-center gap-3 border border-crimson bg-crimson px-7 py-4 text-sm uppercase tracking-[0.15em] text-white transition-colors hover:bg-transparent"
            >
              <Phone className="h-4 w-4" />
              {HOSPITAL.phone1} / {HOSPITAL.phone2.replace("+91 ", "")}
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
